import { FC } from 'react'
import { useLocation } from 'react-router'
import { Link as RouterLink, To } from 'react-router'
import { Box, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import { SystemStyleObject, Theme } from '@mui/system'

import {
  IPS_AQUA,
  NAV_ACTIVE_FOREGROUND,
  NAV_ACTIVE_PILL_GRADIENT,
  NAV_DISABLED_TEXT,
  NAV_HOVER_BG,
  NAV_ICON,
  NAV_ITEM_RADIUS_PX,
  NAV_RAIL_ITEM_WIDTH_PX,
} from '@/common/constants'

// Width of the bar that marks the selected sub-page.
const NESTED_ACCENT_WIDTH_PX = 3

const LABEL_FONT_SIZE = '1.125rem'
const NESTED_LABEL_FONT_SIZE = '1rem'

interface CurbsNavDrawerItemProps {
  drawerOpen: boolean
  /** Omitted by nested items, which are text-only. */
  icon?: any
  testId: string
  title: string
  disabled: boolean
  to?: To
  /**
   * Overrides the default path test. Pass this where the default would be
   * wrong: one item's path is a prefix of another's, or one item has to stay
   * active across several URLs.
   */
  isActive?: boolean
  /** Renders the item as a sub-page in the white flyout: text-only, dark on white, smaller. */
  nested?: boolean
}

const CurbsNavDrawerItem: FC<CurbsNavDrawerItemProps> = ({
  drawerOpen,
  icon,
  testId,
  title,
  disabled,
  to,
  isActive: isActiveOverride,
  nested,
}) => {
  const { pathname } = useLocation()
  const isActive = isActiveOverride ?? (typeof to === 'string' && pathname.startsWith(to))

  /*
    The group that holds a sub-page already carries the white pill in the
    drawer, so the sub-page is marked with an aqua bar instead of a pill of its
    own — two pills read as two selections. The bar and a heavier weight carry
    it: the label keeps the same color as its neighbours.
  */
  const showPill = isActive && !nested

  const iconColor = () => {
    if (disabled) return NAV_DISABLED_TEXT
    return showPill ? NAV_ACTIVE_FOREGROUND : NAV_ICON
  }

  // Sub-pages sit on the white flyout, not on the blue drawer.
  const labelColor = () => {
    if (nested) return disabled ? 'text.disabled' : 'text.primary'
    if (disabled) return NAV_DISABLED_TEXT
    return showPill ? NAV_ACTIVE_FOREGROUND : 'common.white'
  }

  const hoverBackground = () => {
    if (nested) return 'action.hover'
    return showPill ? undefined : NAV_HOVER_BG
  }

  /*
    The whole row is one link, so the icon is clickable too, and the pill lives
    on it rather than on an inner button — collapsed, the label is not rendered
    and the pill still has to show the active state.
  */
  const rowSx: SystemStyleObject<Theme>[] = [
    {
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      height: '40px',
      borderRadius: `${NAV_ITEM_RADIUS_PX}px`,
      background: showPill ? NAV_ACTIVE_PILL_GRADIENT : 'none',
      '&:hover': { backgroundColor: hoverBackground() },
    },
    // A nested item is only ever rendered in the flyout, which always shows
    // labels, so the rail never has to hold one. Its left padding only makes
    // room for the bar.
    drawerOpen
      ? { mx: 2, px: 1.5, ...(nested ? { pl: 3 } : {}) }
      : {
          width: `${NAV_RAIL_ITEM_WIDTH_PX}px`,
          mx: 'auto',
          px: 0,
          justifyContent: 'center',
        },
    // The bar is drawn rather than inset so the label keeps its place: an
    // unselected sub-page lines up with a selected one.
    nested
      ? {
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            left: '12px',
            top: '8px',
            bottom: '8px',
            width: `${NESTED_ACCENT_WIDTH_PX}px`,
            borderRadius: `${NESTED_ACCENT_WIDTH_PX}px`,
            backgroundColor: isActive ? IPS_AQUA : 'transparent',
          },
        }
      : {},
  ]

  const contents = (
    <>
      {icon && (
        <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center', color: iconColor() }}>
          {icon}
        </ListItemIcon>
      )}
      {drawerOpen && (
        <ListItemText
          primary={title}
          slotProps={{
            primary: {
              sx: {
                color: labelColor(),
                fontWeight: isActive ? 600 : 400,
                fontSize: nested ? NESTED_LABEL_FONT_SIZE : LABEL_FONT_SIZE,
              },
            },
          }}
        />
      )}
    </>
  )

  const row =
    disabled || !to ? (
      // Not a ListItemButton: its disabled state kills pointer events, which
      // would take the collapsed tooltip with it.
      <Box aria-disabled sx={[...rowSx, { cursor: 'default' }]}>
        {contents}
      </Box>
    ) : (
      <ListItemButton
        component={RouterLink}
        to={to}
        aria-label={drawerOpen ? undefined : title}
        sx={rowSx}
      >
        {contents}
      </ListItemButton>
    )

  return (
    <ListItem
      // cypress/e2e/4-navigation-pages.cy.ts selects on this class.
      className="curb-drawer-item"
      disablePadding
      sx={{ display: 'block' }}
      data-testid={`curb-nav-section-${testId}`}
      aria-current={isActive ? 'page' : undefined}
    >
      {/* Collapsed there is no label, so the page name has to come from a tooltip. */}
      {drawerOpen ? (
        row
      ) : (
        <Tooltip title={title} placement="right">
          {row}
        </Tooltip>
      )}
    </ListItem>
  )
}

export default CurbsNavDrawerItem
